import React, {useState} from "react";
import type {ExchangeClientReceipt} from "../../types";
import {formatAmount, formatDate} from "../../utils";
import {
    clientReceipts,
    exchangePayoutEscalated,
    exchangePayoutOpen,
    exchangePayoutStatusLabel,
    exchangeWatchRemaining,
    humanSize,
    payoutSentReported,
} from "./logic";
import type {WorkflowStageContext} from "./stage";
import type {StageState} from "./WorkflowStageControls";
import {AmountAndRateFields, RequisitesStage, SectionTitle, stageSectionClass} from "./WorkflowStageControls";

// Покупка USDT: у заявки две ноги. Первая — рубли, и работает по ней трейдер
// только проверкой: платит КЛИЕНТ, он же прикладывает чек. Вторая — перевод
// USDT на адрес клиента; её закрывает третий этап.
//
// Зоны загрузки чека у этого сценария нет вовсе, и это не косметика: чек
// трейдера по такой заявке ничего не подтверждает, сервер его не требует и в
// набор закрытия не берёт. Оставленная зона обещала бы обязанность, которой
// нет, и трейдер грузил бы файлы в никуда.
//
// КАРТОЧКА — ЛЕНТА ИЗ ТРЁХ ЭТАПОВ, идущих в том же порядке, в каком идёт
// работа: реквизиты → оплата клиента → отправка USDT. Все три видны всегда, с
// самого открытия: трейдер должен видеть весь путь, а не открывать его по
// частям. Вкладки, стоявшие здесь раньше, показывали те же этапы, но требовали
// догадаться, что за второй кнопкой, и ставили адрес клиента выше ожидания
// оплаты — то есть в обратном работе порядке.
//
// Пройденный этап остаётся на месте в режиме чтения. Это требование, а не
// удобство: чеки клиента лежат только во втором этапе, и трейдер, не скачавший
// их до подтверждения рублей, достать их больше нигде не может.
export function ExchangeBuy({context}: {context: WorkflowStageContext}): JSX.Element {
    const {payment} = context;
    const requisitesIssued = Boolean(payment.payinRequisites);
    const rublesClosed = exchangePayoutOpen(payment);

    return (
        <>
            <RequisitesStage
                number="1"
                title="Реквизиты приёма"
                hint={requisitesIssued ? "выданы" : "обязательно"}
                state={requisitesIssued ? "done" : "current"}
                context={context}
                readOnly={rublesClosed}
            />
            <ClientPaymentStage context={context} requisitesIssued={requisitesIssued} rublesClosed={rublesClosed}/>
            <PayoutStage context={context} rublesClosed={rublesClosed}/>
        </>
    );
}

// ClientPaymentStage — второй этап: всё про рубли в одном блоке.
//
// Раньше это были три секции подряд — сигналы клиента, подтверждение суммы и
// объяснение про чек, — и связывать их в один этап трейдеру приходилось самому.
// Здесь они собраны: этап ждёт клиента, показывает присланное им и им же
// закрывается.
function ClientPaymentStage({context, requisitesIssued, rublesClosed}: {
    context: WorkflowStageContext;
    requisitesIssued: boolean;
    rublesClosed: boolean;
}): JSX.Element {
    const {payment} = context;
    const exchange = payment.exchange;
    const receipts = clientReceipts(payment);
    const clientMarked = Boolean(exchange?.clientMarkedPaidAt);
    const state: StageState = rublesClosed ? "done" : requisitesIssued ? "current" : "ahead";

    return (
        <section className={stageSectionClass(state)}>
            <SectionTitle number="2" hint={stageTwoHint(state, clientMarked)} state={state}>
                Оплата клиента
            </SectionTitle>

            {rublesClosed && (
                <div className="details-lock-hint" role="status">
                    Рубли закрыты и проведены — этап только для чтения. Отметку клиента
                    об оплате и его чеки можно открыть, менять по этой части заявки уже нечего.
                </div>
            )}

            {clientMarked ? (
                <div className="details-client-signal" role="status">
                    клиент отметил оплату · {formatDate(exchange?.clientMarkedPaidAt ?? "")}
                </div>
            ) : (
                <div className="details-lock-hint" role="status">
                    {requisitesIssued
                        ? "Клиент ещё не отметил оплату."
                        : "Клиент увидит реквизиты после первого этапа."}
                </div>
            )}

            <ClientReceiptList
                receipts={receipts}
                onOpen={context.clientReceipt.onOpen}
                opening={context.clientReceipt.opening}
                error={context.clientReceipt.error}
            />

            {rublesClosed
                ? <RublesDone context={context} receiptCount={receipts.length}/>
                : <RublesForm context={context}/>}
        </section>
    );
}

function stageTwoHint(state: StageState, clientMarked: boolean): string {
    if (state === "done") return "сделано";
    if (state === "ahead") return "откроется после реквизитов";
    return clientMarked ? "подтверждение суммы" : "ожидание оплаты";
}

// RublesForm — сумма, курс и кнопка закрытия рублей.
//
// Кнопка стоит здесь, а не в нижней полосе карточки: этап закрывается своим же
// действием. Решает по-прежнему оболочка — и можно ли закрывать, и что сказать,
// когда нельзя; сюда это приходит готовым, вторая копия правила разошлась бы с
// первой.
function RublesForm({context}: {context: WorkflowStageContext}): JSX.Element {
    const {label, onRequest, canRequest, blockedReason, submitting} = context.complete;

    return (
        <div className="stage-action-card">
            <AmountAndRateFields isIncome context={context}/>
            <div className="details-lock-hint" role="note">
                Свой чек прикладывать не нужно: платил клиент, его чеки выше.
                {blockedReason ? ` ${blockedReason}` : ""}
            </div>
            <div className="requisites-actions">
                <button
                    className="btn btn-success"
                    type="button"
                    onClick={onRequest}
                    disabled={!canRequest || submitting}
                    title={blockedReason}
                >
                    {label}
                </button>
            </div>
        </div>
    );
}

// RublesDone — пройденный этап одной сводкой.
//
// Полей ввода здесь нет намеренно: подтверждать сумму второй раз нечем, сервер
// такой запрос уже не примет. Чеки клиента при этом остаются выше и по-прежнему
// открываются — ради них этап и не сворачивается в строку.
function RublesDone({context, receiptCount}: {
    context: WorkflowStageContext;
    receiptCount: number;
}): JSX.Element {
    const {payment} = context;
    const rate = context.rate.fixed ?? payment.currencyRate;

    return (
        <div className="details-info-card">
            <div className="details-info-row">
                <span>Сумма проведена</span>
                <strong className="mono">{formatAmount(payment.amountReal ?? payment.amount)} ₽</strong>
            </div>
            <div className="details-info-row">
                <span>Курс</span>
                <strong className="mono">{rate ?? "—"}</strong>
            </div>
            <div className="details-info-row">
                <span>Чеков клиента</span>
                <strong className="mono">{receiptCount}</strong>
            </div>
        </div>
    );
}

// ClientReceiptList — что прислал клиент. Пусто — списка нет вовсе: про
// отсутствие чеков уже сказал текст ожидания выше, и повторять это пустой
// карточкой незачем.
function ClientReceiptList({receipts, onOpen, opening, error}: {
    receipts: ExchangeClientReceipt[];
    onOpen: (receiptId: string) => void;
    opening: string | null;
    error: string;
}): JSX.Element {
    return (
        <>
            {receipts.length > 0 && (
                <div className="receipt-ready-list">
                    {receipts.map((receipt, index) => (
                        <div className="receipt-ready-card" key={receipt.id}>
                            <div className="receipt-file-icon">ЧЕК</div>
                            <div className="receipt-file-main">
                                <strong>Чек клиента {index + 1}</strong>
                                <span>{humanSize(receipt.sizeBytes)} · {formatDate(receipt.createdAt)}</span>
                            </div>
                            <button
                                className="btn btn-ghost btn-sm"
                                type="button"
                                onClick={() => onOpen(receipt.id)}
                                disabled={opening === receipt.id}
                            >
                                {opening === receipt.id ? "Открываем..." : "Открыть"}
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {error && <div className="receipt-error">{error}</div>}
        </>
    );
}

// PayoutStage — третий этап: куда, сколько и чем подтверждено.
//
// Адрес клиента виден с самого открытия карточки, ещё до закрытия рублей: он
// известен с создания заявки, и прятать его не от кого. Но стоит он именно
// здесь, ниже второго этапа, а не над ним: доходит до него трейдер последним.
//
// Ни суммы, ни адреса он не вводит: и то и другое — снимок заявки, и поле ввода
// рядом с ними означало бы, что их можно изменить. Единственный ввод — hash
// транзакции, и он же единственное доказательство отправки.
//
// У этапа четыре состояния, и они не косметика:
//
//  1. впереди — рубли не закрыты; поле и кнопка выключены, сервер такой запрос
//     всё равно отвергнет;
//  2. ждём отправки — работает поле и кнопка;
//  3. отправлено, ждём сеть — трейдер сказал, что перевёл, а подтверждает
//     перевод наблюдатель цепочки. Такое состояние бывает ТОЛЬКО при включённом
//     автоподтверждении: без него клик закрывает этап целиком;
//  4. просрочено — окно кончилось, перевода в сети не нашли, этап у админа. Ни
//     поля, ни кнопки: сервер по такой заявке уже откажет, а кнопка, ведущая к
//     отказу, хуже выключенной.
function PayoutStage({context, rublesClosed}: {
    context: WorkflowStageContext;
    rublesClosed: boolean;
}): JSX.Element {
    const {payment} = context;
    const exchange = payment.exchange;
    const {txHash, onTxHashChange, onSend, submitting, blockedReason} = context.exchangePayout;
    const escalated = exchangePayoutEscalated(payment);
    const sentWaitingChain = payoutSentReported(payment);
    const remaining = exchangeWatchRemaining(payment);
    const state: StageState = rublesClosed ? "current" : "ahead";
    // Пока рубли не закрыты, отправлять нечего, и запрет здесь свой: у оболочки
    // причина отказа считается только для открытого этапа, и пустая строка от
    // неё означала бы «препятствий нет».
    const stageLocked = !rublesClosed;
    const lockedHint = "Этап откроется после подтверждения рублей.";

    return (
        <section className={stageSectionClass(state)}>
            <SectionTitle
                number="3"
                hint={rublesClosed ? exchangePayoutStatusLabel(payment) : "откроется после рублей"}
                state={state}
            >
                Отправка USDT
            </SectionTitle>

            {exchange?.payoutAddress && exchange.payoutAmount ? (
                <>
                    <div className="details-info-card">
                        <div className="details-info-row">
                            <span>Сеть</span>
                            <strong className="mono">{exchange.payoutNetwork}</strong>
                        </div>
                        {/* Адрес TRC20 — 34 символа, и это то значение, куда уходят
                            чужие деньги. Своя строка во всю ширину, чтобы трейдер
                            сверял его целиком, а не двумя кусками. */}
                        <div className="details-info-row wide">
                            <span>Адрес</span>
                            <strong className="mono">{exchange.payoutAddress}</strong>
                        </div>
                        <div className="details-info-row">
                            <span>К отправке</span>
                            <strong className="mono">{exchange.payoutAmount} USDT</strong>
                        </div>
                    </div>
                    <CopyAddressButton address={exchange.payoutAddress}/>
                </>
            ) : (
                // Заявка-обмен без второй ноги не должна существовать: строка
                // пишется в одной транзакции с заявкой. Если она всё-таки
                // пришла — трейдеру некуда отправлять USDT, и работать по такой
                // заявке нельзя; кнопки гасит панель.
                <div className="details-lock-hint" role="status">Данные обмена недоступны</div>
            )}

            {escalated ? (
                <div className="details-lock-hint" role="status">
                    Просрочено, передано админу. Окно ожидания перевода истекло — закрыть этап
                    теперь может только администратор.
                </div>
            ) : (
                <>
                    {/* Таймер окна: сколько ещё система ждёт перевод, прежде чем
                        передать этап админу. Пусто — окна нет либо оно истекло, и
                        врать про оставшееся время нечем. */}
                    {remaining && (
                        <div className="details-info-card">
                            <div className="details-info-row">
                                <span>Окно ожидания</span>
                                <strong className="mono">{remaining}</strong>
                            </div>
                        </div>
                    )}
                    {sentWaitingChain ? (
                        <div className="details-info-card">
                            {/* 64 моноширинных символа — строка во всю ширину
                                карточки: в правой колонке она рвётся посередине,
                                а сверять её трейдер будет символ в символ. */}
                            <div className="details-info-row wide">
                                <span>hash транзакции</span>
                                <strong className="mono">{exchange?.payoutTxHash ?? "—"}</strong>
                            </div>
                            <div className="details-info-row">
                                <span>Отправлено</span>
                                <strong>
                                    {exchange?.payoutSentAt ? formatDate(exchange.payoutSentAt) : "—"}
                                </strong>
                            </div>
                        </div>
                    ) : (
                        <div className="stage-action-card">
                            <label className="requisites-field">
                                <span>hash транзакции перевода *</span>
                                <input
                                    className="mono"
                                    type="text"
                                    autoComplete="off"
                                    spellCheck={false}
                                    maxLength={64}
                                    placeholder={stageLocked ? "недоступно до подтверждения рублей" : "64 символа: 0-9 и a-f"}
                                    value={txHash}
                                    onChange={(event) => onTxHashChange(event.target.value)}
                                    disabled={stageLocked || submitting}
                                />
                            </label>
                            {(stageLocked || blockedReason) && (
                                <div className="details-lock-hint" role="status">
                                    {stageLocked ? lockedHint : blockedReason}
                                </div>
                            )}
                            <div className="requisites-actions">
                                <button
                                    className="btn btn-success"
                                    type="button"
                                    onClick={onSend}
                                    disabled={stageLocked || submitting || Boolean(blockedReason)}
                                    title={stageLocked ? lockedHint : blockedReason}
                                >
                                    {submitting ? "Отправляем…" : "USDT отправлены"}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

function CopyAddressButton({address}: {address: string}): JSX.Element {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        void navigator.clipboard?.writeText(address).then(
            () => setCopied(true),
            () => setCopied(false),
        );
    };

    return (
        <div className="requisites-actions">
            <button className="btn btn-ghost btn-sm" type="button" onClick={copy}>
                {copied ? "Адрес скопирован" : "Скопировать адрес"}
            </button>
        </div>
    );
}
